"use client";

import Link from "next/link";
import { useCallback, useEffect, useRef, useState, useTransition } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { ChevronDown, Users as UsersIcon } from "lucide-react";

import { FetchEmptyState } from "@/fetch-ui/FetchEmptyState";
import { FetchSuggestionPill } from "@/fetch-ui/FetchSuggestionPill";
import { OrgAvatar } from "@/features/active-org/_components/OrgAvatar";
import { Avatar } from "@/features/fetch-v2/_components/primitives/Avatar";
import {
  SwitcherSearchPanel,
  type SwitcherOption,
  type SwitcherSearchLabels,
} from "@/features/event-ownership/_components/SwitcherSearchPanel";
import { useSwitcherList } from "@/features/event-ownership/_hooks/use-switcher-list";
import { listRecentUsersAction, searchUsersAction } from "@/features/user-management/_logic/actions/user-actions";
import { MODAL_PAGE_SIZE } from "@/features/user-management/_logic/constants";
import type { Users } from "@/lib/users/types";

import { ADMIN_LAUNCH_PARAM, ADMIN_ORG_ID_PARAM, ADMIN_OWNER_ID_PARAM } from "../_logic/constants";
import { searchOrgAssociatedUsers, type OrgAssociatedUser } from "../_logic/admin-owner-search-actions";

/** Module-scope so the object identity is stable across renders (mirrors `AdminOrgPickerStep`'s own copy).
 *  Shared by both the org-scoped list and the zero-candidate escape hatch's unscoped list — same
 *  entity, same copy either way. */
const USER_SEARCH_LABELS: SwitcherSearchLabels = {
  placeholder: "Find user...",
  listLabel: "Users",
  noun: "users",
};

/** A user's display label: full name when on file, email otherwise — matches `OwnerChangePopover`'s
 *  own fallback so a user with no name still reads sensibly. */
function displayName(user: { full_name: string; email: string }): string {
  return user.full_name || user.email;
}

/** Builds a `SwitcherOption` for a picker row, shared by both the scoped and unscoped lists (both
 *  resolve to the same {user_id, full_name, email, profile_picture_url} shape at the field level). */
function toUserOption(user: {
  user_id: number;
  full_name: string;
  email: string;
  profile_picture_url?: string | null;
}): SwitcherOption {
  const label = displayName(user);
  return {
    key: user.user_id,
    // `Avatar` renders its own accessible name, which would announce the name twice alongside the
    // row's own label — decorative here, same as `OwnerChangePopover`'s equivalent row.
    icon: (
      <span aria-hidden="true" className="flex shrink-0">
        <Avatar name={label} src={user.profile_picture_url ?? null} size={32} />
      </span>
    ),
    label,
    sublabel: user.full_name ? user.email : undefined,
  };
}

interface AdminOwnerPickerStepProps {
  /** `enterprise_organizations.id` for the org already resolved by the entry gate's org step —
   *  scopes the candidate search (KTD12). */
  enterpriseOrgId: number;
  /** Display name for the "for &lt;org&gt;" context line and the unscoped-search notice. */
  orgName: string;
  /** `enterprise_organizations.logo_url`, for the org badge. `OrgAvatar` falls back to a monogram
   *  chip when this is null, matching `AdminOrgPickerStep`'s own list-row treatment. */
  orgLogoUrl: string | null;
  /** The org's square theme icon, shown ahead of `orgLogoUrl` — same icon-then-logo-then-monogram
   *  priority the admin rail's own org switcher (`OrgSwitchPill`) uses. */
  orgIconUrl: string | null;
  /** The org's ENG-870 default owners (KTD10), resolved server-side in `page.tsx` (where the org
   *  is already known and re-authorized) rather than fetched client-side here — renders on first
   *  paint instead of arriving after a separate round trip and shifting the panel below it down. */
  initialSuggestions: OrgAssociatedUser[];
}

/**
 * The owner-picker content for `AdminEntryGate`'s `"owner"` step (ENG-918). Candidates are
 * restricted to users already associated with the resolved org (KTD12, `searchOrgAssociatedUsers`)
 * — a UX convenience mirroring `createEventFromDraft`'s own server-side re-validation (KTD19), not
 * a separate security boundary. `initialSuggestions` (KTD10) surface as suggestion chips above the
 * list, never pre-selected (KTD15: clicking one selects and advances immediately, same as any other
 * pick).
 *
 * When the org genuinely has zero associated users (not just zero search matches), the scoped list
 * is replaced with a distinct empty state offering a "Search all users" fallback (KTD8/R8) that
 * expands to the same unscoped list `ChangeEventOwnerModal`/`OwnerChangePopover` use
 * (`listRecentUsersAction`/`searchUsersAction`). Whoever gets picked that way is accepted
 * server-side by KTD19's own zero-candidate exception.
 *
 * @param props.enterpriseOrgId - The resolved org to scope candidates to.
 * @param props.orgName - Display name for the org.
 * @param props.orgLogoUrl - Logo for the org badge, or `null` to show its monogram fallback.
 * @param props.orgIconUrl - Square icon for the org badge, shown ahead of `orgLogoUrl`.
 * @param props.initialSuggestions - The org's default-owner suggestions, resolved server-side.
 * @returns A prominent org badge (doubling as the "change organization" action), the suggestion
 *   chips (if any), and the picker panel.
 */
export function AdminOwnerPickerStep({
  enterpriseOrgId,
  orgName,
  orgLogoUrl,
  orgIconUrl,
  initialSuggestions,
}: AdminOwnerPickerStepProps): React.ReactElement {
  // Hooks
  const router = useRouter();
  const searchParams = useSearchParams();

  // States
  // Escape hatch (KTD8/R8): flips once the admin explicitly asks to search outside the org scope.
  // Never flips back — once opened, the wider list stays available for the rest of this step.
  const [isUnscoped, setIsUnscoped] = useState(false);
  // A pick is in flight: freezes both panels and every chip/link so a second click can't queue a
  // conflicting navigation while the server-resolved `/create` re-render is still loading.
  const [isNavigating, startNavigation] = useTransition();

  const fetchScopedUsers = useCallback(
    async (term: string, offset = 0): Promise<{ success: boolean; data?: OrgAssociatedUser[] }> => {
      const { users, error } = await searchOrgAssociatedUsers(enterpriseOrgId, term, offset, MODAL_PAGE_SIZE);
      return { success: !error, data: users };
    },
    [enterpriseOrgId]
  );

  const scoped = useSwitcherList<OrgAssociatedUser>({
    isOpen: !isUnscoped,
    listAction: (offset) => fetchScopedUsers("", offset),
    searchAction: (term) => fetchScopedUsers(term),
    // KTD16: an inline notice scoped to this panel (rendered by `SwitcherSearchPanel` as
    // `role="alert"`), matching `AdminOrgPickerStep`'s own copy convention.
    loadErrorMessage: "Couldn't load users. Refresh the page to try again.",
    searchErrorMessage: "Couldn't search users. Try again.",
  });

  const unscoped = useSwitcherList<Users>({
    isOpen: isUnscoped,
    listAction: listRecentUsersAction,
    searchAction: searchUsersAction,
    loadErrorMessage: "Couldn't load users. Refresh the page to try again.",
    searchErrorMessage: "Couldn't search users. Try again.",
  });

  // Derived values
  // "Genuinely zero" (KTD8), not just "this page/search is empty": the unfiltered scoped list
  // finished loading with nothing at all. Gated on an empty search term so an ordinary no-results
  // search (a real candidate exists, this query just didn't match one) never trips this state —
  // `SwitcherSearchPanel`'s own "No users found" empty message covers that case instead. Also gated
  // on there being no load error: `useSwitcherList` renders a failed fetch as an empty list, so
  // without this check a transient `searchOrgAssociatedUsers` failure would show a confident "no
  // users" empty state (steering toward the unscoped escape hatch) instead of surfacing the error —
  // a failed load falls through to the normal panel render below, which already knows how to show
  // `scoped.loadError` via its `notice` prop.
  const isOrgEmpty =
    !isUnscoped &&
    scoped.loadError === null &&
    scoped.search.searchTerm.trim().length === 0 &&
    !scoped.search.isLoading &&
    !scoped.search.hasMore &&
    scoped.search.displayItems.length === 0;

  // "Change organization" drops the org/owner params themselves (that's the whole point of going
  // back to the org step) but keeps everything else this visit arrived with, same as navigateToOwner.
  // It sets `admin_launch` in their place: a visit that arrived org-scoped (`?admin_org_id=` only,
  // see `adminCreateEventHref`) would otherwise be left with no admin-flow signal at all, and
  // `resolveAdminCreateEntryDecision` would skip the org step straight into the normal wizard.
  const changeOrgParams = new URLSearchParams(searchParams);
  changeOrgParams.delete(ADMIN_ORG_ID_PARAM);
  changeOrgParams.delete(ADMIN_OWNER_ID_PARAM);
  changeOrgParams.set(ADMIN_LAUNCH_PARAM, "1");
  const changeOrgHref = `/create?${changeOrgParams.toString()}`;

  // Handlers
  const navigateToOwner = useCallback(
    (userId: number): void => {
      // Preserve every other param this visit arrived with (Northstar's `northstar`/`ns_id`,
      // `fresh`, `resume`) so passing through this gate doesn't silently drop that context.
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set(ADMIN_ORG_ID_PARAM, String(enterpriseOrgId));
      nextParams.set(ADMIN_OWNER_ID_PARAM, String(userId));
      startNavigation(() => {
        router.push(`/create?${nextParams.toString()}`);
      });
    },
    [router, searchParams, enterpriseOrgId]
  );

  // Suggestion chips and the scoped search list both select from the same org-scoped candidate
  // shape, so one handler serves both (KTD15: a chip click selects and advances immediately, same
  // as a search result).
  const handleSelectScoped = useCallback(
    (user: OrgAssociatedUser): void => navigateToOwner(user.user_id),
    [navigateToOwner]
  );
  const handleSelectUnscoped = useCallback((user: Users): void => navigateToOwner(user.user_id), [navigateToOwner]);
  const handleSearchAllUsers = useCallback((): void => setIsUnscoped(true), []);
  const handleChangeOrgClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>): void => {
      if (isNavigating) event.preventDefault();
    },
    [isNavigating]
  );

  // Effects
  // Moves focus to (and announces via `role="status"`) the zero-candidate empty state when it
  // replaces the search panel — without this, focus is dropped when `SwitcherSearchPanel`'s input
  // unmounts, and a screen-reader user gets no notice the list turned into an empty state. Unlike a
  // notice present at initial mount, this one is safe to announce this way: it only ever appears
  // after `isOrgEmpty` flips true post-load, so the live region has genuinely new content.
  const emptyStateRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isOrgEmpty) emptyStateRef.current?.focus();
  }, [isOrgEmpty]);

  // Render
  return (
    <div className="flex flex-col gap-4">
      {/* Prominent org context, since nothing else on this step shows which org the picked owner
          would belong to. Doubles as the "change organization" action — the whole badge is a link
          back to the org step — rather than that being a separate, easy-to-miss text link. */}
      <Link
        href={changeOrgHref}
        onClick={handleChangeOrgClick}
        aria-disabled={isNavigating}
        aria-label={`Organization: ${orgName}. Change organization.`}
        className="inline-flex min-h-[44px] w-fit min-w-0 items-center gap-2 self-start rounded-full border border-gray-200 bg-white px-3 py-1.5 transition-colors hover:bg-gray-50 aria-disabled:pointer-events-none aria-disabled:opacity-50"
      >
        <span aria-hidden="true" className="flex shrink-0">
          <OrgAvatar name={orgName} iconUrl={orgIconUrl} logoUrl={orgLogoUrl} size="xs" />
        </span>
        <span aria-hidden="true" className="truncate text-sm font-medium text-gray-700">
          {orgName}
        </span>
        <ChevronDown aria-hidden="true" className="size-4 shrink-0 text-gray-400" />
      </Link>

      {initialSuggestions.length > 0 && (
        <div className="flex flex-col gap-2">
          <span className="text-xs font-medium text-gray-500">Suggested</span>
          <div className="flex flex-wrap gap-2">
            {initialSuggestions.map((user) => (
              <FetchSuggestionPill
                key={user.user_id}
                icon={<Avatar name={displayName(user)} src={user.profile_picture_url} size={22} />}
                label={displayName(user)}
                onClick={() => handleSelectScoped(user)}
                disabled={isNavigating}
              />
            ))}
          </div>
        </div>
      )}

      {isOrgEmpty ? (
        <div ref={emptyStateRef} tabIndex={-1} role="status" className="outline-none">
          <FetchEmptyState
            icon={<UsersIcon className="h-4 w-4" aria-hidden="true" />}
            title={`${orgName} has no associated users yet.`}
            action={{ label: "Search all users", onClick: handleSearchAllUsers, hideIcon: true }}
          />
        </div>
      ) : isUnscoped ? (
        <>
          <p role="status" className="text-xs font-medium text-gray-500">
            Searching all users, not scoped to {orgName}
          </p>
          <SwitcherSearchPanel
            search={unscoped.search}
            toOption={toUserOption}
            onSelect={handleSelectUnscoped}
            labels={USER_SEARCH_LABELS}
            notice={unscoped.loadError}
            isBusy={isNavigating}
          />
        </>
      ) : (
        <SwitcherSearchPanel
          search={scoped.search}
          toOption={toUserOption}
          onSelect={handleSelectScoped}
          labels={USER_SEARCH_LABELS}
          notice={scoped.loadError}
          isBusy={isNavigating}
        />
      )}
    </div>
  );
}
